import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// axios instance with credentials so httpOnly cookies are sent
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const publicApi = {
  search: (q) => api.get(`/drugs/search`, { params: { q } }).then((r) => r.data),
  recent: (limit = 6) => api.get(`/drugs/recent`, { params: { limit } }).then((r) => r.data),
};

export const adminAuth = {
  gate: (birthdate) => api.post(`/admin/gate`, { birthdate }).then((r) => r.data),
  login: (password, username) =>
    api.post(`/admin/login`, username ? { username, password } : { password }).then((r) => r.data),
  logout: () => api.post(`/admin/logout`).then((r) => r.data),
  logoutAll: () => api.post(`/admin/logout-all`).then((r) => r.data),
  me: () => api.get(`/admin/me`).then((r) => r.data),
  changePassword: (current, new_password, confirm) =>
    api.post(`/admin/change-password`, { current, new_password, confirm }).then((r) => r.data),
};

export const adminApi = {
  list: () => api.get(`/admin/drugs`).then((r) => r.data),
  stats: () => api.get(`/admin/drugs/stats`).then((r) => r.data),
  get: (id) => api.get(`/admin/drugs/${id}`).then((r) => r.data),
  add: (payload) => api.post(`/admin/drugs`, payload).then((r) => r.data),
  update: (id, payload) => api.put(`/admin/drugs/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/admin/drugs/${id}`).then((r) => r.data),
};

// Phase-2 admin analytics — DeepSeek Usage.
// All endpoints require Admin auth (same httpOnly cookies as `adminApi`).
export const deepseekApi = {
  summary: (params) => api.get(`/admin/deepseek/summary`, { params }).then((r) => r.data),
  logs:    (params) => api.get(`/admin/deepseek/logs`,    { params }).then((r) => r.data),
  stats:   (params) => api.get(`/admin/deepseek/stats`,   { params }).then((r) => r.data),
  csvUrl:  (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return `${API}/admin/deepseek/logs.csv${qs ? `?${qs}` : ""}`;
  },
};

// Phase-2 admin analytics — Search Analytics (drug search events).
export const searchAnalyticsApi = {
  summary: (params) => api.get(`/admin/search-analytics/summary`, { params }).then((r) => r.data),
  logs:    (params) => api.get(`/admin/search-analytics/logs`,    { params }).then((r) => r.data),
  stats:   (params) => api.get(`/admin/search-analytics/stats`,   { params }).then((r) => r.data),
  csvUrl:  (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return `${API}/admin/search-analytics/logs.csv${qs ? `?${qs}` : ""}`;
  },
};

// Pricing endpoints for AI models — GET open to any admin (read-only view),
// PUT restricted to super_admin (backend enforces 403).
export const pricingApi = {
  get: (model)        => api.get(`/admin/pricing/${model}`).then((r) => r.data),
  put: (model, body)  => api.put(`/admin/pricing/${model}`, body).then((r) => r.data),
};

// Non-secret admin config — currently only the USD_LEDGER_V2 feature flag.
// Read by AIModelsManagement to decide whether to render the legacy Credits
// UI (V2 OFF) or the new USD ProviderPanel (V2 ON).
export const configApi = {
  get: () => api.get(`/admin/config`).then((r) => r.data),
};

// USD Ledger V2 — internal accounting only. Reads for any admin,
// writes for super_admin. The backend never rejects a user request
// because of this ledger, so the UI is display-only for balance and
// alerts. Costs are computed from real Tokens returned by the models.
export const ledgerApi = {
  overview:        ()               => api.get(`/admin/ledger/overview`).then((r) => r.data),
  providerStats:   (p)              => api.get(`/admin/ledger/provider/${p}/stats`).then((r) => r.data),
  providerEvents:  (p, limit = 50)  => api.get(`/admin/ledger/provider/${p}/events`, { params: { limit } }).then((r) => r.data),
  deposit:         (amount_usd, note) => api.post(`/admin/ledger/deposit`, { amount_usd, note: note || null }).then((r) => r.data),
  setThreshold:    (threshold_usd)  => api.put(`/admin/ledger/threshold`, { threshold_usd }).then((r) => r.data),
  migrate:         (dry_run = true) => api.post(`/admin/ledger/migrate`, null, { params: { dry_run } }).then((r) => r.data),
};

// Admin users management — super_admin only. Returns 403 for others.
export const usersApi = {
  list:       ()          => api.get(`/admin/users`).then((r) => r.data),
  create:     (body)      => api.post(`/admin/users`, body).then((r) => r.data),
  update:     (id, body)  => api.patch(`/admin/users/${id}`, body).then((r) => r.data),
  setPassword:(id, pw)    => api.post(`/admin/users/${id}/password`, {password: pw}).then((r) => r.data),
  logoutAll:  (id)        => api.post(`/admin/users/${id}/logout-all`).then((r) => r.data),
};

export const geminiApi = {
  config: () => api.get(`/admin/gemini/config`).then((r) => r.data),
  updateConfig: (payload) => api.put(`/admin/gemini/config`, payload).then((r) => r.data),
  // Legacy Credits endpoints (balance, addTopup, topups, summary, logs, stats,
  // csvUrl) removed 2026-02 — USD Ledger V2 is the sole accounting layer and
  // exposes its own endpoints via ledgerApi / providerApi. The two methods
  // above remain because GeminiLimitsCard writes limit toggles through them.
};
